import { useMemo, useState } from "react";

import { Users } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { TablePagination } from "~/components/ui/table-pagination";
import { usePagination } from "~/hooks/usePagination";
import { AT_CLIENT_STATUSES, TotemWithRelations } from "~/services/totemService";

import { MoveTotemsDialog } from "./MoveTotemsDialog";

interface ClientTotemsTabProps {
  totems: TotemWithRelations[];
  isLoading?: boolean;
}

interface ClientTotemGroup {
  clientId: string;
  clientName: string;
  count: number;
}

const PAGE_SIZE = 10;

export function ClientTotemsTab({ totems, isLoading }: ClientTotemsTabProps) {
  const [selected, setSelected] = useState<{
    id: string;
    name: string;
    count: number;
  } | null>(null);

  // Aggregate: one row per client holding totems (at their site), highest first.
  const groups = useMemo<ClientTotemGroup[]>(() => {
    const map = new Map<string, ClientTotemGroup>();
    for (const totem of totems) {
      if (
        !totem.current_client_id ||
        !AT_CLIENT_STATUSES.includes(totem.status)
      ) {
        continue;
      }
      const existing = map.get(totem.current_client_id);
      if (existing) {
        existing.count += 1;
      } else {
        map.set(totem.current_client_id, {
          clientId: totem.current_client_id,
          clientName: totem.client?.name ?? "Cliente sin nombre",
          count: 1,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [totems]);

  const { page, setPage, pageCount, pageItems, total } = usePagination(
    groups,
    PAGE_SIZE
  );

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle>Tótems por Cliente</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
            </div>
          ) : groups.length === 0 ? (
            <div className="p-6">
              <EmptyState
                icon={Users}
                title="Ningún cliente tiene tótems"
                description="Cuando un cliente reciba tótems aparecerá aquí con su cantidad."
              />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cliente</TableHead>
                    <TableHead className="text-right">Tótems</TableHead>
                    <TableHead className="w-[120px] text-right">
                      Acción
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageItems.map((group) => (
                    <TableRow
                      key={group.clientId}
                      className="hover:bg-muted/30"
                    >
                      <TableCell className="font-medium">
                        {group.clientName}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {group.count}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-9"
                          onClick={() =>
                            setSelected({
                              id: group.clientId,
                              name: group.clientName,
                              count: group.count,
                            })
                          }
                        >
                          Mover
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {pageCount > 1 && (
                <div className="border-t p-4">
                  <TablePagination
                    page={page}
                    pageCount={pageCount}
                    totalItems={total}
                    pageSize={PAGE_SIZE}
                    onPageChange={setPage}
                    itemLabel="clientes"
                  />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <MoveTotemsDialog
        source={
          selected
            ? {
                statuses: AT_CLIENT_STATUSES,
                count: selected.count,
                clientId: selected.id,
                clientName: selected.name,
              }
            : null
        }
        destinations={["disponible", "en_transito", "lavado", "con_problema"]}
        open={selected !== null}
        onOpenChange={(o) => !o && setSelected(null)}
      />
    </>
  );
}
